import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Eye, X, Radio, MessageCircle, Flame } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import useArenaxRealtime from './hooks/useArenaxRealtime';
import ArenaxReactionDock from './ArenaxReactionDock';
import ArenaxKickoffCountdown from './ArenaxKickoffCountdown';
import ArenaxCelebrationOverlay from './ArenaxCelebrationOverlay';
import ArenaxCommentaryOverlay, { ArenaxHotMicComposer } from './ArenaxCommentaryOverlay';
import ArenaxClipReel from './ArenaxClipReel';

/**
 * Phase 2L PR 5 — Spectator Room overlay route.
 *
 * Lightweight, kinetic watch-party UX.  Reuses the match WebSocket channel for
 * shared reactions + spectator presence + live commentary snippets.  Closes
 * automatically when the match ends.
 *
 * Mounted at /arenax/matches/:matchId/spectate.
 */
export default function ArenaxSpectatorRoom() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [commentary, setCommentary] = useState([]);
  // Phase 2M PR 2 — goal-pulse + earned celebration overlay
  const [goalPulse, setGoalPulse] = useState(false);
  const [celebrationEvent, setCelebrationEvent] = useState(null);
  const prevScoreRef = useRef({ a: null, b: null });
  const commentaryEndRef = useRef(null);
  // Phase 2N PR 3 — captain detection (hot-mic composer)
  const [myCaptainSquadIds, setMyCaptainSquadIds] = useState([]);

  // Realtime — subscribe to the match channel ONLY after the room loaded AND
  // is open (avoids doomed WS handshakes for missing matches + closed rooms).
  const rt = useArenaxRealtime({ channel: 'match', channelId: matchId,
                                    enabled: !!matchId && !!room && room.status === 'open' });

  // Initial room fetch + join
  useEffect(() => {
    if (!matchId) return;
    let isMounted = true;
    setLoading(true);
    api.get(`/arenax/matches/${matchId}/room`)
      .then(r => {
        if (!isMounted) return;
        const next = r.data?.room;
        setRoom(next);
        setCommentary(next?.commentary || []);
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setLoading(false); });
    // Try to register as a spectator (silently OK if unauth)
    setJoining(true);
    api.post(`/arenax/matches/${matchId}/spectate`, undefined, { suppressErrorToast: true })
      .then(r => { if (isMounted && r.data?.room) {
        setRoom(r.data.room);
        setCommentary(r.data.room.commentary || []);
      }})
      .catch(() => {})
      .finally(() => { if (isMounted) setJoining(false); });
    return () => { isMounted = false; };
  }, [matchId]);

  // Append live commentary lines from realtime broadcasts
  useEffect(() => {
    if (!rt?.lastReaction) return;
    // (reactions are already shown via the ArenaxReactionDock + spectator stream)
  }, [rt?.lastReaction]);

  useEffect(() => {
    // Subscribe to spectator_commentary frames pushed via the match channel
    const msg = rt?.spectatorCommentary;
    if (!msg || !msg.line) return;
    setCommentary(prev => [...prev.slice(-24), { at: msg.at || new Date().toISOString(), line: msg.line }]);
  }, [rt?.spectatorCommentary]);

  // Phase 2M PR 2 — Goal-pulse: fire a quick amber flash when the score increments
  useEffect(() => {
    const mp = rt?.matchProgress;
    if (!mp) return;
    const sa = mp.score_a;
    const sb = mp.score_b;
    if (sa == null && sb == null) return;
    const prev = prevScoreRef.current;
    const incremented = (prev.a != null && sa > prev.a) || (prev.b != null && sb > prev.b);
    prevScoreRef.current = { a: sa, b: sb };
    if (incremented) {
      setGoalPulse(true);
      setTimeout(() => setGoalPulse(false), 900);
    }
  }, [rt?.matchProgress]);

  // Phase 2M PR 2 — Earned celebration overlay on culturally significant events
  useEffect(() => {
    const ev = rt?.tournamentEvent;
    if (!ev) return;
    if (ev.type === 'match_completed' && ev.is_final) {
      setCelebrationEvent({ type: 'championship_clinch',
                               line: `${ev.winner_name || 'Champions'} take it. The trophy is theirs.` });
    } else if (ev.type === 'match_completed' && ev.is_semifinal) {
      setCelebrationEvent({ type: 'semifinal_win',
                               line: `${ev.winner_name || 'A squad'} is into the final.` });
    } else if (ev.type === 'streak_broken') {
      setCelebrationEvent({ type: 'streak_broken',
                               line: ev.line || 'A long unbeaten run just ended.' });
    } else if (ev.type === 'comeback_clinched') {
      setCelebrationEvent({ type: 'comeback_clinched',
                               line: ev.line || 'A comeback nobody saw coming.' });
    }
  }, [rt?.tournamentEvent]);

  // Auto-scroll the commentary feed
  useEffect(() => {
    if (commentaryEndRef.current) {
      commentaryEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [commentary.length]);

  // Phase 2N PR 3 — fetch the user's captain squad ids (one-shot)
  useEffect(() => {
    if (!user?.id || !user) return;
    api.get('/arenax/squads/mine', { suppressErrorToast: true })
      .then(r => {
        const captainOf = (r.data?.squads || [])
          .filter(s => s.role === 'captain' || s.role === 'co_captain')
          .map(s => s.id);
        setMyCaptainSquadIds(captainOf);
      })
      .catch(() => {});
  }, [user?.id, user?.role]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300 text-sm"
        data-testid="arenax-spectator-room-loading">
        Loading the stadium…
      </div>
    );
  }
  if (!room) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 text-center"
        data-testid="arenax-spectator-room-missing">
        <div className="max-w-md">
          <Radio className="mx-auto h-8 w-8 text-slate-500 mb-3" />
          <h3 className="text-lg font-black text-slate-100 mb-1">No live room here.</h3>
          <p className="text-sm text-slate-400 mb-4">This match isn't live right now. Catch the next one from the city feed.</p>
          <Link to="/arenax/city"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400">
            Open city feed
          </Link>
        </div>
      </div>
    );
  }

  const spectatorCount = (room.spectators || []).length;
  const isClosed = room.status === 'closed';
  // Phase 2N PR 3 — Hot-mic composer renders for captains of either squad
  const canPinHotMic = !isClosed && (
    myCaptainSquadIds.includes(room.squad_a_id) || myCaptainSquadIds.includes(room.squad_b_id)
  );
  // The most recent commentary line drives the overlay classifier
  const latestLine = commentary.length > 0 ? commentary[commentary.length - 1].line : null;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="arenax-spectator-room">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-slate-950/85 border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            data-testid="arenax-spectator-room-close"
            className="p-2 rounded-lg hover:bg-slate-800/60 text-slate-400 hover:text-slate-100 transition-colors">
            <X className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.25em] text-amber-300 font-black inline-flex items-center gap-1">
              <Radio className={`h-3 w-3 ${isClosed ? '' : 'animate-pulse'}`} />
              {isClosed ? 'CLOSED' : 'WATCHING LIVE'}
            </div>
            <h1 className="text-base sm:text-lg font-black text-slate-100 truncate">
              {room.squad_a_name} <span className="text-slate-500">vs</span> {room.squad_b_name}
            </h1>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30"
            data-testid="arenax-spectator-room-count">
            <Eye className="h-3 w-3 text-cyan-300" />
            <span className="text-xs font-black text-cyan-200 tabular-nums">{spectatorCount}</span>
          </span>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Phase 2N PR 3 — Live commentary overlay (momentum / rivalry / champ / hot-mic) */}
        <ArenaxCommentaryOverlay
          matchId={matchId}
          captainHotMic={rt?.captainHotMic}
          latestCommentary={latestLine}
        />
        {/* Phase 2M PR 2 — Kickoff Countdown (only when match scheduled & imminent) */}
        {room.kickoff_imminent && room.seconds_to_kickoff > 0 && (
          <div className="mb-5">
            <ArenaxKickoffCountdown
              initialSeconds={room.seconds_to_kickoff}
              spectatorCount={spectatorCount}
              matchLabel={`${room.squad_a_name} vs ${room.squad_b_name}`}
            />
          </div>
        )}

        {/* Phase 2M PR 2 — Goal-pulse: brief amber wash on score change */}
        {goalPulse && (
          <div className="pointer-events-none fixed inset-0 z-30"
            data-testid="arenax-goal-pulse"
            style={{ animation: 'arenax-goal-pulse-flash 900ms ease-out' }}>
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(closest-side at 50% 30%, rgba(245,158,11,0.28), transparent 70%)',
            }} />
            <style>{`
              @keyframes arenax-goal-pulse-flash {
                0%   { opacity: 0; }
                15%  { opacity: 1; }
                100% { opacity: 0; }
              }
            `}</style>
          </div>
        )}

        {/* Live commentary feed */}
        <section className="rounded-2xl border border-slate-800 p-5 mb-5"
          data-testid="arenax-spectator-room-commentary"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="h-4 w-4 text-cyan-300" />
            <span className="text-[10px] tracking-[0.3em] text-cyan-300 font-bold uppercase">
              Live commentary
            </span>
          </div>
          {commentary.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Tip-off pending — the room will start whispering when kickoff hits.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {commentary.map((c, idx) => (
                <p key={`${c.at}-${idx}`}
                  data-testid={`arenax-spectator-room-line-${idx}`}
                  className="text-sm text-slate-200 leading-snug border-l-2 border-slate-700 pl-3"
                  style={{
                    animation: 'arenax-commentary-enter 350ms ease-out both',
                  }}>
                  {c.line}
                </p>
              ))}
              <div ref={commentaryEndRef} />
            </div>
          )}
          {/* Phase 2N PR 3 — Captain Hot Mic composer (captains only) */}
          <ArenaxHotMicComposer matchId={matchId} canPin={canPinHotMic} />
        </section>

        {/* Phase 2O PR 2 — Clip Reel + Uploader (vertical-first creator tools) */}
        <ArenaxClipReel matchId={matchId} canUpload={!!user?.id && user?.role === 'player'} />

        {/* Spectators */}
        <section className="rounded-2xl border border-slate-800 p-5 mb-5"
          data-testid="arenax-spectator-room-people">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-cyan-300" />
              <span className="text-[10px] tracking-[0.3em] text-cyan-300 font-bold uppercase">
                In the stands
              </span>
            </div>
            {joining && <span className="text-[10px] text-slate-500 italic">joining…</span>}
          </div>
          {spectatorCount === 0 ? (
            <p className="text-sm text-slate-500 italic">Be the first one in.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(room.spectators || []).slice(0, 20).map((s, idx) => (
                <span key={`${s.user_id}-${idx}`}
                  data-testid={`arenax-spectator-${idx}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 border border-slate-700 text-[10px] font-bold text-slate-300">
                  {(s.user_name || 'P').split(' ')[0]}
                </span>
              ))}
              {spectatorCount > 20 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900 border border-slate-700 text-[10px] font-bold text-slate-500">
                  +{spectatorCount - 20}
                </span>
              )}
            </div>
          )}
        </section>

        {/* Open match page link */}
        <div className="flex items-center justify-center">
          <Link to={`/arenax/tournaments/${room.tournament_id}`}
            data-testid="arenax-spectator-room-open-match"
            className="text-xs font-bold text-amber-300 hover:text-amber-200">
            Open the tournament →
          </Link>
        </div>
      </div>

      {/* Reaction dock — fixed at bottom */}
      <ArenaxReactionDock targetType="match" targetId={matchId} />

      {/* Phase 2M PR 2 — Earned celebration overlay (rare-by-design) */}
      <ArenaxCelebrationOverlay event={celebrationEvent} onDismiss={() => setCelebrationEvent(null)} />

      <style>{`
        @keyframes arenax-commentary-enter {
          from { opacity: 0; transform: translateX(-4px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
